import React, { useEffect, useState } from "react";
import { api, brl } from "../../lib/api";
import { Activity, ShieldCheck, FlaskConical, CalendarClock } from "lucide-react";
import CreditTabs from "./CreditTabs";
import AIStatusIndicator from "../../components/AIStatusIndicator";

const PURPLE = "#5B2CA0";
const DEEP = "#2B1E4A";

const GATE_LABEL = {
  COLETANDO_DADOS: "Coletando dados", AMOSTRA_INSUFICIENTE: "Amostra insuficiente",
  EM_VALIDACAO: "Em validação", ELEGIVEL_PARA_REVISAO: "Elegível para avaliação",
};
const GATE_COLOR = {
  COLETANDO_DADOS: "#8A7788", AMOSTRA_INSUFICIENTE: "#C2560A",
  EM_VALIDACAO: "#B7891B", ELEGIVEL_PARA_REVISAO: "#2E7D46",
};

const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);

export default function CreditPerformance() {
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/credit/performance/overview").then(({ data }) => setOv(data))
      .catch((e) => setErr(e.response?.data?.detail || "Falha ao carregar performance."));
  }, []);

  return (
    <div className="p-6" data-testid="credit-performance-page">
      <CreditTabs markSeen />
      <div className="mb-4 flex items-center gap-2">
        <Activity size={22} color={PURPLE} />
        <div>
          <h1 className="font-display text-2xl font-bold" style={{ color: DEEP }}>Performance Real & Prontidão</h1>
          <p className="text-[12px] text-[#8A7788]">Analytics do motor em DRY-RUN. Nada é ativado automaticamente.</p>
        </div>
      </div>
      {err && <div className="rounded-xl bg-[#FDECEC] p-3 text-sm text-[#8B5E5E]" data-testid="perf-error">{err}</div>}
      {!ov ? <div className="text-sm text-[#8A7788]">Carregando...</div> : (
        <>
          <AIStatusIndicator />
          <GateCard gate={ov.gate} />
          <AssistedSimCard sim={ov.assisted_simulation} />
          <PerformanceExplorer overview={ov} />
          <RulerTable ruler={ov.ruler} maturation={ov.maturation} />
        </>
      )}
    </div>
  );
}

function GateCard({ gate }) {
  if (!gate) return null;
  return (
    <section className="rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-gate">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><ShieldCheck size={16} color={PURPLE} /><h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Prontidão do Motor</h2></div>
        <span className="rounded-full px-3 py-1 text-[12px] font-bold text-white" style={{ background: GATE_COLOR[gate.state] }} data-testid="gate-state">{GATE_LABEL[gate.state] || gate.state}</span>
      </div>
      {gate.message && <div className="mb-3 rounded-lg bg-[#F1FAF3] px-3 py-2 text-[12px] font-semibold text-[#2E7D46]" data-testid="gate-message">{gate.message}</div>}
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {gate.criteria.map((c) => (
          <div key={c.key} className="flex items-center justify-between border-b border-[#F2EFEA] py-1.5 text-[12px]" data-testid={`gate-crit-${c.key}`}>
            <span className="text-[#5C5A57]">{c.label}</span>
            <span className={`font-bold ${c.met ? "text-[#2E7D46]" : "text-[#B4564F]"}`}>
              {typeof c.current === "object"
                ? (Object.keys(c.current).length ? Object.entries(c.current).map(([k, v]) => `${k}: ${v}`).join(" · ") : "0")
                : (c.current < 1 && c.current > 0 ? pct(c.current) : c.current)}
              {" / "}{c.target < 1 && c.target > 0 ? pct(c.target) : c.target} {c.met ? "✓" : "✗"}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-[#8A7788]" data-testid="gate-note">{gate.note}</p>
    </section>
  );
}

function AssistedSimCard({ sim }) {
  if (!sim) return null;
  return (
    <section className="mt-6 rounded-2xl border border-[#E7DBF7] bg-[#FBF9FF] p-5" data-testid="credit-assisted-sim">
      <div className="mb-3 flex items-center gap-2"><FlaskConical size={16} color={PURPLE} /><h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Se ASSISTED estivesse ativo hoje…</h2></div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Aumentos" value={sim.increases_count} testid="sim-increases" />
        <Kpi label="Crédito adicional" value={brl(sim.additional_credit)} testid="sim-additional" />
        <Kpi label="Clientes" value={sim.clients_involved} testid="sim-clients" />
        <Kpi label="Score médio" value={sim.avg_score} testid="sim-avgscore" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-[#5C5A57]">
        <span>Presença Cris: <b>{sim.business_unit_presence?.CRIS_LINGERIE ?? 0}</b></span>
        <span>Presença Eletros: <b>{sim.business_unit_presence?.ELETROS ?? 0}</b></span>
      </div>
      <p className="mt-2 text-[11px] text-[#8A7788]">{sim.note}</p>
    </section>
  );
}

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-xl border border-[#EBE5DF] bg-white p-3 text-center" data-testid={testid}>
    <div className="text-xl font-bold" style={{ color: DEEP }}>{value}</div>
    <div className="text-[10px] text-[#8A7788]">{label}</div>
  </div>
);

function PerformanceExplorer({ overview }) {
  const avail = overview.windows_available || {};
  const firstAvail = [30, 60, 90].find((w) => (avail[String(w)] || 0) > 0) || 30;
  const [win, setWin] = useState(firstAvail);
  const [scope, setScope] = useState("GLOBAL");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const available = (avail[String(win)] || 0) > 0;
  useEffect(() => {
    if (!available) { setDetail(null); return; }
    setLoading(true);
    api.get(`/credit/performance?window=${win}&scope=${scope}`).then(({ data }) => setDetail(data))
      .catch(() => setDetail(null)).finally(() => setLoading(false));
  }, [win, scope, available]);

  const mat = overview.maturation || {};
  return (
    <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-performance-real">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Performance Real</h2>
        <div className="flex gap-1" data-testid="perf-window-tabs">
          {[30, 60, 90].map((w) => {
            const ok = (avail[String(w)] || 0) > 0;
            return (
              <button key={w} onClick={() => setWin(w)} disabled={!ok} data-testid={`perf-win-${w}`}
                className={`rounded-lg px-3 py-1 text-[12px] font-bold ${win === w ? "text-white" : "text-[#5C5A57] border border-[#D6CFC7]"} ${!ok ? "opacity-40 cursor-not-allowed" : ""}`}
                style={win === w ? { background: PURPLE } : {}}>{w}d {ok ? `(${avail[String(w)]})` : ""}</button>
            );
          })}
        </div>
      </div>
      {!available ? (
        <div className="rounded-xl bg-[#FBFAFE] p-4 text-[12px] text-[#8A7788]" data-testid="perf-maturing">
          Coorte em maturação para {win} dias.
          {mat.days_until_first_30d != null && <> Próxima janela (30d) em <b>{mat.days_until_first_30d}</b> dia(s).</>}
          {" "}Decisões acompanhadas: <b>{mat.total_monitored ?? 0}</b>.
        </div>
      ) : (
        <>
          <div className="mb-3 flex gap-1" data-testid="perf-scope-tabs">
            {[["GLOBAL", "Global"], ["CRIS_LINGERIE", "Cris Lingerie"], ["ELETROS", "Eletros"]].map(([k, l]) => (
              <button key={k} onClick={() => setScope(k)} data-testid={`perf-scope-${k}`}
                className={`rounded-lg px-3 py-1 text-[12px] font-bold ${scope === k ? "text-white" : "text-[#5C5A57] border border-[#D6CFC7]"}`}
                style={scope === k ? { background: DEEP } : {}}>{l}</button>
            ))}
          </div>
          {loading || !detail ? <div className="text-[12px] text-[#8A7788]">Carregando...</div> : (
            <div data-testid="perf-detail">
              {!detail.performance.available ? (
                <div className="rounded-xl bg-[#FBFAFE] p-4 text-[12px] text-[#8A7788]">Coorte em maturação.</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Kpi label="Amostra" value={detail.performance.sample} testid="perf-sample" />
                    <Kpi label="Positivos" value={pct(detail.performance.positive_rate)} testid="perf-positive" />
                    <Kpi label="Negativos" value={pct(detail.performance.negative_rate)} testid="perf-negative" />
                    <Kpi label="Inadimplência pós-aumento" value={pct(detail.performance.default_rate_after_increase)} testid="perf-default" />
                    <Kpi label="Utilização média" value={pct(detail.performance.avg_utilization)} testid="perf-util" />
                    <Kpi label="Vendido após aumento" value={brl(detail.performance.sold_after)} testid="perf-sold" />
                    <Kpi label="Recebido após aumento" value={brl(detail.performance.received_after)} testid="perf-received" />
                  </div>
                  <div className="mt-4">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#8A7788]">Por faixa de score</div>
                    {detail.by_band.map((b) => (
                      <div key={b.band} className="flex items-center justify-between border-b border-[#F2EFEA] py-1.5 text-[12px]" data-testid={`perf-band-${b.band}`}>
                        <span className="text-[#5C5A57]">{b.band}</span>
                        {b.sufficient ? (
                          <span className="font-bold" style={{ color: DEEP }}>+{pct(b.positive_rate)} · −{pct(b.negative_rate)} <span className="text-[#8A7788]">({b.sample})</span></span>
                        ) : (
                          <span className="text-[#8A7788]">{b.note} ({b.sample})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function RulerTable({ ruler, maturation }) {
  if (!ruler || ruler.length === 0) {
    return (
      <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-ruler">
        <div className="mb-2 flex items-center gap-2"><CalendarClock size={16} color={PURPLE} /><h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Régua de Amadurecimento</h2></div>
        <div className="rounded-xl bg-[#FBFAFE] p-4 text-[12px] text-[#8A7788]">Nenhuma decisão aplicada em acompanhamento.</div>
      </section>
    );
  }
  const STAGE = { aguardando_30d: "Aguardando 30d", "30d": "30 dias", "60d": "60 dias", "90d": "90 dias" };
  return (
    <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-ruler">
      <div className="mb-3 flex items-center gap-2"><CalendarClock size={16} color={PURPLE} /><h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Régua de Amadurecimento</h2></div>
      <div className="space-y-2">
        {ruler.slice(0, 40).map((r) => (
          <div key={r.decision_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#EBE5DF] px-3 py-2" data-testid={`ruler-row-${r.decision_id}`}>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-bold" style={{ color: DEEP }}>{r.client_name || "Cliente"}</div>
              <div className="text-[11px] text-[#8A7788]">Decisão {new Date(r.decision_date).toLocaleDateString("pt-BR")} · {brl(r.old_limit)} → {brl(r.new_limit)} · score {r.score}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-[#EDE3FB] px-2 py-0.5 text-[10px] font-bold text-[#5B2CA0]">{STAGE[r.stage] || r.stage}</span>
              <span className="text-[11px] text-[#5C5A57]">{r.next_evaluation === "concluída" ? "Concluída (90d)" : `Próx.: ${r.next_evaluation} em ${r.days_until_next}d`}</span>
            </div>
          </div>
        ))}
      </div>
      {maturation?.days_until_first_30d != null && (
        <p className="mt-3 text-[11px] font-bold" style={{ color: PURPLE }}>Primeira coorte de 30 dias fecha em {maturation.days_until_first_30d} dia(s).</p>
      )}
    </section>
  );
}
